import React, { useState, useEffect } from 'react'
import Confetti from 'react-confetti' // Import react-confetti for success animation
import './styles.css'
import { CloseButton } from 'react-bootstrap'

interface MatchingGameProps {
  options: any
  answer: any
  handleNextExample: () => void // Function to handle the next example
}

const MatchingGame: React.FC<MatchingGameProps> = ({ options, answer, handleNextExample }) => {
  const [cards, setCards] = useState<string[]>([])
  const [clickedCards, setClickedCards] = useState<{ [key: number]: string }>({})
  const [successModalShow, setSuccessModalShow] = useState(false)
  const [wrongModalShow, setWrongModalShow] = useState(false)
  const [progress, setProgress] = useState(0) // Track the progress of the countdown
  const [confetti, setConfetti] = useState(false) // State to trigger confetti animation
  const [wrongAnimation, setWrongAnimation] = useState(false) // State to handle wrong answer animation

  const uniqueElements = ['🔴', '⭐', '🟢']

  useEffect(() => {
    const initialDeck = [...options, ...uniqueElements, ...options, ...uniqueElements].slice(0, 12)
    const deck = secureShuffle(initialDeck)
    setCards(deck)
  }, [options])

  function secureShuffle(array: any) {
    const arr = [...array]
    const randomValues = new Uint32Array(arr.length)
    crypto.getRandomValues(randomValues)

    for (let i = arr.length - 1; i > 0; i--) {
      const j = randomValues[i] % (i + 1)
      ;[arr[i], arr[j]] = [arr[j], arr[i]]
    }

    return arr
  }

  useEffect(() => {
    if (successModalShow) {
      const timer = setTimeout(() => {
        handleNext() // Transition to the next example
      }, 5000)

      const progressInterval = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress < 100) return prevProgress + 2
          return prevProgress
        })
      }, 100)

      return () => {
        clearTimeout(timer)
        clearInterval(progressInterval)
      }
    }
  }, [successModalShow])

  useEffect(() => {
    if (wrongModalShow) {
      const timer = setTimeout(() => {
        handleRetry() // Reset the game when wrong modal closes
      }, 5000)

      const progressInterval = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress < 100) return prevProgress + 2
          return prevProgress
        })
      }, 100)

      return () => {
        clearTimeout(timer)
        clearInterval(progressInterval)
      }
    }
  }, [wrongModalShow])

  const handleCardClick = (card: string, index: number) => {
    if (clickedCards[index]) return // Prevent clicking on already clicked cards

    const isCorrect = card === answer
    setClickedCards((prevState) => ({
      ...prevState,
      [index]: isCorrect ? 'success' : 'danger'
    }))

    if (isCorrect) {
      setConfetti(true) // Show confetti on success
      setWrongAnimation(false) // Reset wrong animation
      setSuccessModalShow(true) // Show success modal
    } else {
      setWrongAnimation(true) // Show wrong answer animation
      setConfetti(false) // Stop confetti on failure
      setWrongModalShow(true) // Show wrong answer modal
    }
  }

  const handleRetry = () => {
    setClickedCards({})
    setWrongModalShow(false)
    setWrongAnimation(false)
  }

  const handleNext = () => {
    handleNextExample()
    setClickedCards({})
    setSuccessModalShow(false)
    setConfetti(false)
  }

  const handleModalClose = () => {
    setClickedCards({})
    setSuccessModalShow(false)
    setWrongModalShow(false)
    setConfetti(false)
    setWrongAnimation(false)
    setProgress(0)
  }

  const renderCardContent = (card: string) => {
    if (uniqueElements.includes(card)) {
      // Render unique elements (e.g., emojis)
      return <span style={{ fontSize: '2rem' }}>{card}</span>
    } else {
      // Render image if the card is not a unique element
      return (
        <img
          src={card}
          alt='card'
          style={{ width: '100%', height: '100%', borderRadius: '8px', objectFit: 'cover' }}
        />
      )
    }
  }

  return (
    <div className='d-flex flex-wrap justify-content-end col-2 col-md-4'>
      {cards.map((card, index) => (
        <div
          key={index}
          className={`card m-3 ${wrongAnimation ? 'wrong-animation' : ''}`}
          style={{
            width: '100px',
            height: '100px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: clickedCards[index]
              ? clickedCards[index] === 'success'
                ? 'green'
                : 'red'
              : '#83d174',
            border: '1px solid #ccc',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'transform 0.2s'
          }}
          onClick={() => handleCardClick(card, index)}
        >
          {renderCardContent(card)}
        </div>
      ))}

      {confetti && <Confetti recycle={false} numberOfPieces={800} />}

      {successModalShow && (
        <div className='party-animation'>
          <div className='party-popup'>
            <div className='text-end close-button-container mb-2'>
              <CloseButton onClick={handleModalClose} />
            </div>
            <h2>सही उत्तर! आपने सही विकल्प चुना है।</h2>
            <div className='party-cracker'>🎉</div>
            <button onClick={handleNext} className='btn btn-success mt-4'>
              धन्यवाद
            </button>
            <div className='progress-bar success'>
              <div className='progress-bar-inner' style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      )}

      {wrongModalShow && (
        <div className='wrong-animation-popup'>
          <div className='wrong-popup'>
            <div className='text-end close-button-container mb-2'>
              <CloseButton onClick={handleModalClose} />
            </div>
            <h2>अरे नहीं! सही उत्तर नहीं था!</h2>
            <div className='sad-face'>😢</div>
            <button onClick={handleRetry} className='btn btn-danger mt-4'>
              फिर से प्रयास करें
            </button>
            <div className='progress-bar wrong'>
              <div className='progress-bar-inner' style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MatchingGame
